import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Button, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { Pagination } from "../../DataTable";
import CargoContenedorModal from "../Admin/CargosContenedor/CargoContenedorModal";
import DocumentosContenedor from "../Revalidaciones/DocumentosContenedor";
import { COLOR_CLASE, URL_FINANZAS, auth, fmt } from "./finanzasUtils";

const POR_PAGINA = 50;
const CLASES = ["Nota", "Cargo", "Abono", "Gasto", "Pago"];
const COMPROBANTE = [{ tipo: "comprobanteAbono", label: "Comprobante de pago", icon: "fas fa-receipt", grupo: "Abono" }];

// Bitácora de todo el dinero del periodo, con el comprobante de cada abono y la edición de cargos
export default function TabMovimientos({ user, refresco, onCambio }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [inicio, setInicio] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(moment().format("YYYY-MM-DD"));
  const [clase, setClase] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [editando, setEditando] = useState(null);
  const [comprobante, setComprobante] = useState(null);

  useEffect(() => {
    setCargando(true);
    axios
      .get(`${URL_FINANZAS}Movimientos/${inicio}/${fin}`, auth())
      .then(({ data }) => setFilas(data))
      .catch((err) => {
        setFilas([]);
        Swal.fire({ icon: "error", title: "No se pudieron cargar los movimientos", text: err?.response?.data?.message || err.message });
      })
      .finally(() => setCargando(false));
  }, [inicio, fin, refresco]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return filas.filter(
      (f) =>
        (!clase || f.clase === clase) &&
        (!q || [f.contenedor, f.referencia, f.tercero, f.detalle].some((x) => String(x || "").toLowerCase().includes(q)))
    );
  }, [filas, clase, busca]);

  const totalDe = (c) => filas.filter((f) => f.clase === c).reduce((s, f) => s + f.importe, 0);

  const excel = () => {
    const datos = visibles.map((f) => ({
      Fecha: f.fecha, Movimiento: f.clase, Folio: f.folio, Contenedor: f.contenedor, Referencia: f.referencia,
      ClienteBroker: f.tercero, Detalle: f.detalle, Importe: f.importe, Saldo: f.saldo === undefined ? "" : f.saldo,
    }));
    const cols = Object.keys(datos[0] || { Fecha: "" });
    new ExportJsonExcel({ fileName: `MovimientosFinanzas-${inicio}-${fin}`, datas: [{ sheetData: datos, sheetName: "Movimientos", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  return (
    <>
      <div className="filter-bar">
        <div>
          <Label>Fecha inicio</Label>
          <Input bsSize="sm" type="date" value={inicio} onChange={(e) => { setInicio(e.target.value); setPagina(1); }} />
        </div>
        <div>
          <Label>Fecha fin</Label>
          <Input bsSize="sm" type="date" value={fin} onChange={(e) => { setFin(e.target.value); setPagina(1); }} />
        </div>
        <div>
          <Label>Movimiento</Label>
          <Input bsSize="sm" type="select" value={clase} onChange={(e) => { setClase(e.target.value); setPagina(1); }}>
            <option value="">Todos</option>
            {CLASES.map((x) => <option key={x}>{x}</option>)}
          </Input>
        </div>
        <div>
          <Label>Buscar</Label>
          <Input bsSize="sm" type="text" value={busca} onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
        </div>
        <div className="fz-excel">
          <Button size="sm" onClick={excel} disabled={!visibles.length}><i className="far fa-file-excel" /> Excel</Button>
        </div>
      </div>

      <div className="stats-row">
        {CLASES.map((c) => (
          <button key={c} type="button" className={`stat-chip fz-chip${clase === c ? " on" : ""}`} onClick={() => { setClase(clase === c ? "" : c); setPagina(1); }}>
            {c === "Nota" ? "Notas" : `${c}s`} <span>{fmt(totalDe(c))}</span>
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando movimientos…</div></div>
      ) : visibles.length === 0 ? (
        <div className="rc-state"><i className="fas fa-check-circle" /><div>No hay movimientos con estos filtros.</div></div>
      ) : (
        <>
          <div className="table-wrapper">
            <Table size="sm" hover className="table-responsive-xl">
              <thead>
                <tr>
                  <th>Fecha</th><th>Movimiento</th><th>Folio</th><th>Contenedor</th><th>Referencia</th><th>Cliente / Broker</th><th>Detalle</th>
                  <th className="td-num">Importe</th><th className="td-num">Saldo</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA).map((f) => (
                  <tr key={`${f.clase}-${f._id}`}>
                    <td>{f.fecha}</td>
                    <td><span className={`badge-soft badge-soft-${COLOR_CLASE[f.clase]}`}>{f.clase}</span></td>
                    <td>{f.folio}</td>
                    <td className="td-strong">{f.contenedor}</td>
                    <td>{f.referencia}</td>
                    <td>{f.tercero}</td>
                    <td>{f.detalle}</td>
                    <td className="td-num td-money">{fmt(f.importe)}</td>
                    <td className="td-num td-money">{f.saldo === undefined ? "—" : fmt(f.saldo)}</td>
                    <td>
                      {f.clase === "Abono" ? (
                        <Button
                          size="sm"
                          color={f.comprobante === "Si" ? "success" : "secondary"}
                          title={f.comprobante === "Si" ? "Ver comprobante de pago" : "Subir comprobante de pago"}
                          onClick={() => setComprobante(f)}
                        >
                          <i className="fas fa-receipt" />
                        </Button>
                      ) : null}
                      {f.clase === "Cargo" && user.menuAdmin && f.cargo.abonos === 0 ? (
                        <Button size="sm" color="info" title="Editar cargo" onClick={() => setEditando(f.cargo)}><i className="fas fa-edit" /></Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination total={visibles.length} itemsPerPage={POR_PAGINA} currentPage={pagina} onPageChange={setPagina} />
        </>
      )}

      <CargoContenedorModal
        isOpen={!!editando}
        toggle={() => setEditando(null)}
        user={user}
        contenedor={editando ? { ...editando.contenedor, idClientes: editando.idClientes, cliente: editando.cliente } : null}
        cargo={editando}
        onSaved={onCambio}
      />

      <Modal size="lg" isOpen={!!comprobante} toggle={() => setComprobante(null)}>
        <ModalHeader toggle={() => setComprobante(null)}>
          <span>Comprobante de pago · Abono {comprobante ? comprobante.folio : ""}</span>
        </ModalHeader>
        <ModalBody>
          {comprobante ? (
            <DocumentosContenedor
              contenedorId={comprobante._id}
              documentos={COMPROBANTE}
              docs={{ comprobanteAbono: comprobante.comprobante }}
              puedeSubir={!!user.abonosCreate}
              onChange={(docs) => {
                const valor = docs.comprobanteAbono;
                setComprobante((x) => ({ ...x, comprobante: valor }));
                setFilas((fs) => fs.map((x) => (x.clase === "Abono" && x._id === comprobante._id ? { ...x, comprobante: valor } : x)));
              }}
            />
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
}
